import { Injectable } from '@angular/core';
import { getDeviceInfo } from 'src/app/shared/utils/device-info.utils';

/**
 * Service for managing focus behavior across the application.
 */
@Injectable({
  providedIn: 'root'
})
export class FocusManagementService {
  private userHasInteracted = false;
  private initialEntryFocusBlocked = true;
  private readonly focusableSelector =
    'textarea, input, select, button, a[href], [tabindex]:not([tabindex="-1"])';

  constructor() {
    // Track any user-initiated focus to avoid overriding manual focus
    if (typeof document !== 'undefined') {
      document.addEventListener(
        'focusin',
        () => {
          this.userHasInteracted = true;
        },
        { passive: true }
      );
    }
  }

  isMobileDevice(): boolean {
    if (typeof window === 'undefined') return false;

    return getDeviceInfo().isMobile;
  }

  /**
   * Checks if the user prefers reduced motion based on OS/browser settings.
   * Used to skip animation delays for autofocus timing (WCAG 2.3.3 compliance).
   * @returns true if user prefers reduced motion
   */
  prefersReducedMotion(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  hasUserInteracted(): boolean {
    return this.userHasInteracted;
  }

  isInitialEntryFocusBlocked(): boolean {
    return this.initialEntryFocusBlocked;
  }

  canApplyProgrammaticFocus(): boolean {
    return !this.initialEntryFocusBlocked && !this.userHasInteracted;
  }

  blockInitialEntryFocus(): void {
    this.initialEntryFocusBlocked = true;
  }

  releaseInitialEntryFocusBlock(): void {
    this.initialEntryFocusBlocked = false;
  }

  resetInteractionState(): void {
    this.userHasInteracted = false;
  }

  findFirstFocusableDeep(
    root: ParentNode | null,
    options: { includeRoot?: boolean } = {}
  ): HTMLElement | null {
    if (!root) {
      return null;
    }

    if (
      options.includeRoot &&
      root instanceof HTMLElement &&
      this.isFocusable(root)
    ) {
      return root;
    }

    if (root instanceof Element && root.shadowRoot) {
      const shadowMatch = this.findFirstFocusableDeep(root.shadowRoot);
      if (shadowMatch) {
        return shadowMatch;
      }
    }

    for (const child of Array.from(root.children)) {
      const match = this.findFirstFocusableDeep(child, {
        includeRoot: true
      });

      if (match) {
        return match;
      }
    }

    return null;
  }

  focusFirstFocusableDeep(
    root: ParentNode | null,
    options: { includeRoot?: boolean } = {}
  ): HTMLElement | null {
    const focusable = this.findFirstFocusableDeep(root, options);
    focusable?.focus();
    return focusable;
  }

  private isFocusable(element: HTMLElement): boolean {
    return (
      element.matches(this.focusableSelector) &&
      !this.isDisabled(element) &&
      this.isVisible(element)
    );
  }

  private isDisabled(element: HTMLElement): boolean {
    return (
      element.hasAttribute('disabled') ||
      element.getAttribute('aria-disabled') === 'true' ||
      ('disabled' in element &&
        Boolean((element as HTMLInputElement | HTMLButtonElement).disabled))
    );
  }

  private isVisible(element: HTMLElement): boolean {
    if (typeof window === 'undefined') {
      return true;
    }

    let current: HTMLElement | null = element;

    while (current) {
      const style = window.getComputedStyle(current);
      if (
        current.hidden ||
        current.getAttribute('aria-hidden') === 'true' ||
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        parseFloat(style.opacity) === 0
      ) {
        return false;
      }

      current = this.getParentElementAcrossShadowBoundary(current);
    }

    return element.getClientRects().length > 0;
  }

  private getParentElementAcrossShadowBoundary(
    element: HTMLElement
  ): HTMLElement | null {
    if (element.parentElement) {
      return element.parentElement;
    }

    const root = element.getRootNode();
    return root instanceof ShadowRoot && root.host instanceof HTMLElement
      ? root.host
      : null;
  }
}
